// DayEndSheet — what a crew member sees before anything is closed.
//
// The owner asked for a review, not a bulk button: "show to the worker … which
// will be marked completed and which will be moved to the next day with the
// uncomplete tasks - show tasks per job." So this sheet has three jobs and
// they are all about being READ, at arm's length, outdoors:
//
//   1. Say what will happen, per job, in two named groups plus the ones being
//      left alone. A job that is missing from a review is a job somebody finds
//      still open tomorrow morning, so nothing is hidden.
//   2. Name the tasks. "2 tasks left" is the answer that sent them looking.
//      Every carrying job lists its open tasks by title.
//   3. Be a LOOP. Tapping a job goes to it, and the sheet remembers itself so
//      pressing the button again is not a hunt (dayEnd.js rememberReview).
//
// ── 2026-09-11: the job FINISHES either way ────────────────────────────────
//
// The second group used to mean "this job moves to tomorrow". The owner
// rejected that — "When completing the job, the job is complete" — so it now
// means "this job is done, and the items nobody ticked go to a new job
// tomorrow". Every job in the review that is not held is being COMPLETED, and
// the sheet says both halves on the job itself rather than only in a heading,
// because the heading is the part a person scrolls past.
//
// It also says when the SET RATE follows the work. `jobs.pay_amount` is what
// the WORKER is paid (server/utils/paystubCalc.js resolveEntryPay falls back
// to it), and on a set-rate job it moves to the job that finishes the work.
// The FACT is drawn; the FIGURE is not, because no /api/field endpoint sends
// one and whether a phone should show a pay figure is a separate decision.
//
// Every word of it comes from the server's plan. This component computes no
// outcome, no count and no date — see dayEnd.js. It is handed a plan and it
// draws it, so the modal cannot say something the database disagrees with.
//
// A 409 is not an error here. It is the review again, with the fresh plan in
// the body: something changed while the crew member was looking, so the sheet
// redraws and says so rather than blanking or, worse, going ahead.

import React from 'react';
import { Sheet, FwButton, Notice, SectionLabel, Pill, Skeleton, EmptyState } from '../../ui/fw';
import { Icon } from '../../ui';
import { DAY_END_GROUPS, carryItemsTitle, confirmLabel, jobsWithOutcome, planHasWork, dayEndAction } from './dayEnd';

/** One job in the review: what it is, and what is about to happen to it. */
function PlanJob({ job, onOpen }) {
  const where = [job.client_name, job.property_name].filter(Boolean).join(' · ');
  const open = job.open_subtasks || [];
  const carrying = job.outcome === 'carry_items';
  return (
    <div className="fd-de__job" data-outcome={job.outcome} data-job-id={job.id}>
      <button
        type="button"
        className="fd-de__open"
        onClick={() => onOpen(job)}
        aria-label={`Open ${job.title}`}
      >
        <span className="fd-de__jobtext">
          <span className="fd-de__title">{job.title}</span>
          {where ? <span className="fd-de__where">{where}</span> : null}
        </span>
        <span className="fd-de__chev" aria-hidden="true"><Icon name="chevronRight" /></span>
      </button>

      {/* Both halves of what happens to THIS job, in plain words, before it
          happens — and the confirmation afterwards repeats the same two facts.
          The owner asked twice for the modal to say what is being completed
          AND what is being moved, and a job that says only one of them is the
          half-answer that made the old model read as "the job is moving". */}
      {carrying ? (
        <div className="fd-de__outcome">
          <span className="fd-de__finish">
            <Icon name="check" /> This job is done
          </span>
          {job.moves_to_label ? (
            <span className="fd-de__moves">
              {open.length} item{open.length === 1 ? '' : 's'} left — moving to a new job on {job.moves_to_label}
            </span>
          ) : null}
          {/* Said out loud rather than inferred. On a SET-RATE job the amount
              the worker is paid moves to the job that finishes the work, and a
              crew member watching this one complete should not have to work
              that out. The figure is not shown: no /api/field endpoint sends a
              pay figure, and whether one should is a separate decision. */}
          {job.pay_moves ? (
            <span className="fd-de__pay">
              <Icon name="dollar" /> The set rate for this job moves with them
            </span>
          ) : null}
        </div>
      ) : null}

      {/* The server's own sentence. Never a code, never a blank. */}
      {job.hold_reason ? <div className="fd-de__why">{job.hold_reason}</div> : null}

      {open.length > 0 ? (
        <ul className="fd-de__tasks">
          {open.map((t) => (
            <li key={t.id} className="fd-de__task">
              <span className="fd-de__taskbox" aria-hidden="true" />
              <span className="fd-de__tasktext">{t.title}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {job.outcome === 'complete' && job.subtask_total > 0 ? (
        <div className="fd-de__done">
          <Icon name="check" /> {job.subtask_done} of {job.subtask_total} done
        </div>
      ) : null}
    </div>
  );
}

function Group({ group, plan, onOpen }) {
  const jobs = jobsWithOutcome(plan, group.key);
  if (jobs.length === 0) return null;
  const title = group.key === 'carry_items' ? carryItemsTitle(plan) : group.title;
  return (
    <div className="fd-de__group" data-group={group.key}>
      <SectionLabel>
        {title} <Pill tone={group.tone}>{jobs.length}</Pill>
      </SectionLabel>
      {jobs.map((j) => <PlanJob key={j.id} job={j} onOpen={onOpen} />)}
    </div>
  );
}

export default function DayEndSheet({
  open,
  scope,
  plan,
  loading,
  error,
  applied,
  busy,
  online = true,
  onOpenJob,
  onConfirm,
  onClose,
  onRetry,
}) {
  if (!open) return null;
  const action = dayEndAction(scope);
  const hasWork = planHasWork(plan);
  const blocked = plan && plan.available === false;

  let body;
  if (loading && !plan) {
    body = <Skeleton lines={5} />;
  } else if (error && !plan) {
    body = (
      <Notice tone="stop" title="Could not work out what is left">
        {error}{' '}
        {onRetry ? <FwButton variant="quiet" onClick={onRetry}>Try again</FwButton> : null}
      </Notice>
    );
  } else if (!plan) {
    body = <Skeleton lines={5} />;
  } else {
    body = (
      <>
        {/* What was actually done, from the server's answer — kept on screen
            with the fresh review underneath it, so "pressed it, did it work?"
            is answered without leaving. */}
        {applied ? <Notice tone="ok" title="Done">{applied}</Notice> : null}
        {error ? <Notice tone="stop">{error}</Notice> : null}
        {blocked ? <Notice tone="warn">{plan.available_reason}</Notice> : null}
        {!online ? (
          <Notice tone="warn">
            Closing jobs needs a connection. Ticks, notes and photos are saved on this phone as usual.
          </Notice>
        ) : null}

        <div className="fd-de__when">{plan.window_label}</div>

        {plan.jobs.length === 0 ? (
          <EmptyState icon="check" title="Nothing left to close">
            {scope === 'week' ? 'Every job this week is closed or moved on.' : 'Every job today is closed or moved on.'}
          </EmptyState>
        ) : (
          DAY_END_GROUPS.map((g) => <Group key={g.key} group={g} plan={plan} onOpen={onOpenJob} />)
        )}
      </>
    );
  }

  return (
    <Sheet
      open
      title={action ? action.heading : 'End of day'}
      onClose={onClose}
      className="fd-de"
      footer={(
        <>
          <FwButton variant="quiet" onClick={onClose} disabled={busy}>
            {hasWork ? 'Not yet' : 'Close'}
          </FwButton>
          {hasWork ? (
            <FwButton
              variant="go"
              onClick={onConfirm}
              loading={busy}
              disabled={!online || blocked}
              aria-label="Confirm and close these jobs"
              data-day-end-confirm
            >
              {/* The button says what it is about to do, with the numbers from
                  the plan on it — not "OK". Two numbers: jobs closing, and
                  ITEMS moving, which are different counts. dayEnd.js owns the
                  wording so the sentence has one home. */}
              {confirmLabel(plan)}
            </FwButton>
          ) : null}
        </>
      )}
    >
      {body}
    </Sheet>
  );
}
